import { Checkbox, Dialog, Popover, Tooltip } from "@mui/material";
import { ArrowLeft, Edit, Filter, PlusCircle } from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import API from "../../../API/newIndex";
import BulkEditPosLocation from "./BulkEditPosLocation";
import { DataLoad, PlanPermission } from "../../../App";
import Breadcrumbs from "../../../controllers/breadCrumbs";
import { formatDate } from "../../../controllers/FormatAmount";
import { clientData } from "../../../controllers/GetClient";
import { parentNavigate } from "../../../controllers/ParentNavigate";
import { TableColumnsContext } from "../../../controllers/TableColumnsContext";
import { useUser } from "../../../controllers/UserContext";
import NoRecords from "../../Main/NoRecords";
import Notification from "../../Notification";
import {
  ButtonComponent,
  CommonTable,
  PageLimitDropDownComponent,
  PaginationComponent,
  SearchComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import { isAscendingOrder } from "../../../utilities/staticFunction";
import RenderCustomFieldValue from "../../../utilities/RenderCustomFieldValue";
import * as defaultCustomFields from '../../../utilities/defaultCustomTableField';
import useTableApi from "../../../utilities/useTableApi";

const PosLocations = () => {
  const handleTableApi = useTableApi();
  const location = useLocation();
  const { dataLoading, setDataLoading } = useContext(DataLoad);
  const { setPlanPermission } = useContext(PlanPermission);
  const [locations, setLocations] = useState();
  const [Page, setPage] = useState(1);
  const initialLimit = localStorage.getItem("limit") || "10";
  const [limit, setLimit] = useState(initialLimit);
  const [selectedRowsId, setSelectedRowsId] = useState([]);
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [isUpgradePopup, setIsUpgradePopup] = useState(false);
  const currentLocationCount = locations?.length || 0;
  const staffLimit = clientData?.plan_data?.no_of_location || 0;
  const isLimitReached =
    staffLimit !== "Unlimited" && currentLocationCount >= staffLimit;

  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();

  const savedSort = sortData?.poslocation;

  const [sortBy, setSortBy] = useState(
    () => (location?.state?.accending ? location?.state?.field : savedSort?.sort_by || "createdAt")
  );
  const [orderBy, setOrderBy] = useState(() => {
    if (location?.state?.accending) {
      return { orderByValue: "ASC", isDecending: false };
    }
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? "ASC" : "DESC") : "DESC",
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isInitialSortLoaded = useRef(Boolean(location?.state?.field || savedSort?.sort_by));

  const { tableColumnsData } = useContext(TableColumnsContext);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.poslocation)) return dataObj.poslocation;
    if (Array.isArray(tableColumnsData?.poslocation)) return tableColumnsData.poslocation;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      "_id",
      "__v",
      "client",
      "tenant",
      "store_client",
      "store_tenant",
      "user",
      "name",
      "language",
      "staff",
      "createdAt",
      "updatedAt",
      "action",
      "actions",
      "checkbox",
      "selection",
      "select",
      "drag_handle",
      "draghandle",
      "drag",
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === "string" && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes(".");
        const parts = isDotted ? fieldName.split(".") : null;

        const label = fieldName
          .replace(/\./g, " › ")
          .replace(/[-_]/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: "160px",
          minWidth: "110px",
          responsiveWidth: "100px",
          headerClassName: "px-3 whitespace-nowrap truncate",
          cellClassName: "px-3 truncate",
          render: (row) => (
            <RenderCustomFieldValue
              val={getValue(row)}
              fieldName={fieldName}
              clientData={clientData}
            />
          ),
        };
      });
  }, [apiFieldNames]);

  useEffect(() => {
    localStorage.setItem("limit", limit);
  }, [limit]);

  useEffect(() => {
    if (!isInitialSortLoaded.current && isUserLoaded) {
      isInitialSortLoaded.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        if (savedSort.sort_by !== sortBy || targetOrderBy !== orderBy.orderByValue) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: targetOrderBy,
            isDecending: !isAsc,
          });
        }
      }
    }
  }, [savedSort, isUserLoaded, sortBy, orderBy.orderByValue]);

  // Sorting Handler
  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "ASC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "ASC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  };

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  const [search, setSearch] = useState(
    location?.state?.searchkey ? location?.state?.searchkey : "",
  );
  useEffect(() => {
    setSearch(location?.state?.searchkey ? location?.state?.searchkey : "");
  }, [location?.state?.searchkey]);

  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (!isUserLoaded && !isInitialSortLoaded.current && !location?.state?.field) {
      return;
    }

    const trimmed = search?.trim() || "";
    clearTimeout(debounceTimeout.current);
    if (trimmed === "") {
      getLocations();
    } else if (trimmed.length >= 3) {
      debounceTimeout.current = setTimeout(getLocations, 500);
    }
    return () => clearTimeout(debounceTimeout.current);
  }, [search, sortBy, orderBy, isUserLoaded]);

  const getLocations = async () => {
    setDataLoading(true);
    try {
      const orderParam = orderBy?.orderByValue || (orderBy?.isDecending ? "DESC" : "ASC");
      let ApiCall = await API({
        url: `/admin/pos/getPOSLocations?searchkey=${search?.length > 2 ? search : ""}&sort_by=${sortBy}&order_by=${orderParam}`,
        method: "GET",
      });
      if (ApiCall.status === 200 || ApiCall.status === 304) {
        const result = ApiCall?.data;
        setLocations(result);
      }
    } catch (error) {
      if (error?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "pos" });
      }
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    } finally {
      setDataLoading(false);
    }
  };

  const Handleedit = (id) => {
    parentNavigate(`/pos/location/${id}`);
  };
  const [filter, setFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const resetFilter = (e) => {
    e.preventDefault();
    setSearch(location?.state?.searchkey ? location?.state?.searchkey : "");
    setPage(1);
    setLimit(limit === "all" ? "10" : limit);
    setFilter(false);
    setAnchorEl(null);
  };

  const handleSelectRow = (e, id) => {
    if (e?.nativeEvent?.shiftKey) {
      const prevIndex = locations?.findIndex(
        (x) => x._id === selectedRowsId[selectedRowsId?.length - 1],
      );
      const currIndex = locations?.findIndex((x) => x._id === id);
      const startIndex = Math.min(prevIndex, currIndex);
      const endIndex = Math.max(prevIndex, currIndex);
      const shiftSelected = locations?.slice(startIndex, endIndex + 1);

      if (selectedRowsId?.includes(id)) {
        setSelectedRowsId((prev) => [
          ...new Set(prev.filter((x) => !shiftSelected.find((item) => item._id === x))),
        ]);
      } else {
        setSelectedRowsId((prev) => [
          ...new Set([...prev, ...shiftSelected.map((item) => item._id)]),
        ]);
      }
    } else if (id === "all") {
      if (selectedRowsId?.length > 0) {
        setSelectedRowsId([]);
      } else {
        setSelectedRowsId(locations?.map((x) => x?._id));
      }
    } else {
      const existId = selectedRowsId?.includes(id);
      if (!existId) {
        setSelectedRowsId((prev) => [...prev, id]);
      } else {
        setSelectedRowsId((prev) => prev.filter((x) => x !== id));
      }
    }
  };

  // Dynamic sorting applied to local records
  const sortedLocations = useMemo(() => {
    if (!locations || !Array.isArray(locations)) return [];
    return [...locations].sort((a, b) => {
      let aVal = a?.[sortBy];
      let bVal = b?.[sortBy];
      if (aVal === undefined || aVal === null) aVal = "";
      if (bVal === undefined || bVal === null) bVal = "";
      if (typeof aVal === "string") {
        const cmp = aVal.localeCompare(String(bVal));
        return orderBy.orderByValue === "ASC" ? cmp : -cmp;
      }
      if (aVal < bVal) return orderBy.orderByValue === "ASC" ? -1 : 1;
      if (aVal > bVal) return orderBy.orderByValue === "ASC" ? 1 : -1;
      return 0;
    });
  }, [locations, sortBy, orderBy]);

  const totalRecords = sortedLocations?.length || 0;
  const limitValue = limit === "all" ? totalRecords || 10 : Number(limit || 10);
  const paginatedLocations = limit === "all"
    ? sortedLocations
    : sortedLocations?.slice((Page - 1) * limitValue, Page * limitValue);

  const columns = [
    {
      key: "checkbox",
      width: "40px",
      minWidth: "40px",
      maxWidth: "40px",
      responsiveWidth: "40px",
      headerClassName: "!px-0 w-[40px] min-w-[40px] max-w-[40px] text-center align-middle",
      cellClassName: "!px-0 w-[40px] min-w-[40px] max-w-[40px] text-center align-middle",
      align: "text-center",
      ignoreRowClick: true,
      title: (
        <Checkbox
          checked={
            locations?.length > 0 &&
            selectedRowsId?.length === locations?.length
          }
          indeterminate={
            selectedRowsId?.length > 0 &&
            selectedRowsId?.length < locations?.length
          }
          onChange={(e) => handleSelectRow(e, "all")}
        />
      ),
      render: (row) => (
        <Checkbox
          checked={selectedRowsId?.length > 0 && selectedRowsId?.includes(row._id)}
          onChange={(e) => handleSelectRow(e, row._id)}
        />
      ),
    },
    {
      key: "name",
      title: t("lang_Location") || "Location",
      width: "250px",
      minWidth: "180px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="font-medium text-xs text-[var(--primary-text-color)]">
          {row?.name ?? "-"}
        </span>
      ),
    },
    {
      key: "language",
      title: t("lang_language") || "Language",
      width: "160px",
      minWidth: "120px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="text-xs text-[var(--table-text-color)]">
          {row?.language ?? "-"}
        </span>
      ),
    },
    {
      key: "staff",
      title: t("lang_staff") || "Staff",
      width: "160px",
      minWidth: "120px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="text-xs text-[var(--table-text-color)]">
          {row?.staff ?? "-"}
        </span>
      ),
    },
    {
      key: "createdAt",
      title: t("lang_created_date") || "Created Date",
      width: "160px",
      minWidth: "130px",
      headerClassName: "px-3 whitespace-nowrap",
      cellClassName: "px-3 whitespace-nowrap",
      render: (row) => (
        <span className="table-text text-xs whitespace-nowrap">
          {row?.createdAt ? formatDate(row.createdAt) : "-"}
        </span>
      ),
    },
    ...dynamicColumns,
    {
      key: "action",
      title: t("lang_action") || "Action",
      width: "100px",
      minWidth: "80px",
      maxWidth: "120px",
      headerClassName: "px-3 text-right",
      cellClassName: "px-3 text-right",
      align: "text-right",
      ignoreRowClick: true,
      render: (row) => (
        <div className="space-x-2 w-full flex items-center justify-end">
          {row?.email !== clientData?.business_email && (
            <Tooltip title="Edit" arrow>
              <ButtonComponent
                variant="ghost"
                size="sm"
                onClick={() => Handleedit(row?._id)}
                className="!p-1 text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
              >
                <Edit className="w-[17px] h-[17px]" />
              </ButtonComponent>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const tableHeaderFilter = (manageFieldsBtn) => {
    return (
      <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
        <div className="flex flex-wrap items-center justify-between w-full gap-2 min-h-[38px]">
          <div className="flex items-center gap-2 ml-auto hidden md:flex">
            <div className="w-[180px] md:w-[200px]">
              <SearchComponent
                placeholder={t("lang_Search_Location") || "Search Location"}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearch("");
                  setPage(1);
                }}
                isClearable={true}
                size="sm"
                iconPlacement="left"
              />
            </div>
            <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
              <PageLimitDropDownComponent
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                totalRecords={totalRecords}
              />
            </div>
            {manageFieldsBtn}
          </div>

          <Tooltip title={t("lang_filter") || "Filter"} arrow>
            <ButtonComponent
              variant="outline"
              size="sm"
              onClick={(e) => {
                setFilter(true);
                setAnchorEl(e.currentTarget);
              }}
              className="block md:hidden relative !p-1.5 w-8 h-8 shrink-0 ml-auto"
            >
              <Filter className="h-4 w-4 text-[var(--table-text-color)]" />
            </ButtonComponent>
          </Tooltip>
        </div>

        {selectedRowsId?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t main-border-color w-full">
            <span className="text-xs px-2 whitespace-nowrap font-medium">
              {selectedRowsId?.length} {t("lang_selected") || "Selected"}
            </span>
            <span className="text-gray-300">|</span>
            <ButtonComponent
              variant="outline"
              size="sm"
              className="mx-1"
              onClick={() => setOpenBulkEditModal(true)}
              icon={<Edit className="w-3.5 h-3.5" />}
            >
              {t("lang_bulk_edit", "Bulk Edit")}
            </ButtonComponent>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="flex items-center justify-between w-full overflow-auto pb-4">
        <div className="flex items-center space-x-2">
          <ButtonComponent
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              parentNavigate("/dashboard");
            }}
            className="back-arrow !p-1.5"
          >
            <ArrowLeft />
          </ButtonComponent>
          <div>
            <Breadcrumbs
              crumbs={[
                { name: t("lang_dashboard"), path: `/dashboard` },
                { name: t("lang_Location"), path: "/pos/location" },
              ]}
            />
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1">
              <h4 className="text-xl md:text-2xl font-semibold text-primary">{t("lang_Location")}</h4>
            </div>
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex gap-2 items-center justify-center">
            {isLimitReached && (
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" className="injected-svg" color="CurrentColor">
                <path fillRule="evenodd" clipRule="evenodd" d="M12.0242 3.20436C7.15097 3.20436 3.20456 7.1445 3.20456 11.9998C3.20456 16.8551 7.15097 20.7953 12.0242 20.7953C15.6407 20.7953 18.7495 18.6247 20.1108 15.5168C20.3273 15.0224 20.9036 14.7972 21.398 15.0137C21.8924 15.2303 22.1176 15.8066 21.9011 16.301C20.2388 20.0962 16.4424 22.7498 12.0242 22.7498C6.07606 22.7498 1.25002 17.9392 1.25002 11.9998C1.25002 6.06048 6.07606 1.24982 12.0242 1.24982C16.7393 1.24982 20.7464 4.2717 22.2073 8.48006C22.4777 9.25891 22.6608 10.0781 22.7452 10.9258C22.776 11.2346 22.6581 11.5397 22.4278 11.7478C22.1975 11.9558 21.882 12.0421 21.5778 11.9802L19.177 11.4915C18.816 11.4181 18.5269 11.1478 18.4294 10.7925C18.332 10.4372 18.4426 10.0573 18.7155 9.80984L19.8123 8.81568C19.9676 8.67488 20.0453 8.60448 20.0641 8.50595C20.083 8.40743 20.0389 8.31753 19.9506 8.13775C18.5165 5.21614 15.5056 3.20436 12.0242 3.20436Z" fill="CurrentColor"></path>
                <path fillRule="evenodd" clipRule="evenodd" d="M9.50001 10.2491V9.18741C9.50001 7.81574 10.6452 6.74991 12 6.74991C13.3548 6.74991 14.5 7.81574 14.5 9.18741V10.2491C14.5751 10.2719 14.6498 10.2992 14.7239 10.3321C15.3441 10.6073 15.8211 11.1276 16.0663 11.7619C16.1722 12.0359 16.2129 12.3172 16.2318 12.6138C16.25 12.8993 16.25 13.2471 16.25 13.6638V13.711C16.25 14.1277 16.25 14.4755 16.2318 14.761C16.2129 15.0576 16.1722 15.339 16.0663 15.6129C15.8211 16.2473 8.6559 10.6073 9.27608 10.3321C9.35024 10.2992 9.42488 10.2719 9.50001 10.2491ZM11 9.18741C11 8.69512 11.4218 8.24991 12 8.24991C12.5782 8.24991 13 8.69512 13 9.18741V10.1249C12.9675 10.1249 12.9345 10.1249 12.9011 10.1249H11.0989C11.0655 10.1249 11.0325 10.1249 11 10.1249V9.18741Z" fill="CurrentColor"></path>
              </svg>
            )}
            <ButtonComponent
              variant="primary"
              icon={<PlusCircle className="w-4 h-4" />}
              onClick={() => {
                if (isLimitReached) {
                  setIsUpgradePopup(true);
                  return;
                }
                parentNavigate("/pos/location/add");
              }}
            >
              {t("lang_Add_Location")}
            </ButtonComponent>
          </div>
        </div>
      </div>
      {isUpgradePopup && (
        <div className="relative inset-0 z-[9999]">
          <div className="bg-white rounded-lg shadow-lg w-[350px] p-6 text-center absolute right-1 top-[-25px]">
            <p className="text-lg font-semibold mb-2">Upgrade Required 🚀</p>
            <p className="text-sm text-gray-600 mb-5">
              You have reached your Location limit. Upgrade your plan to add more Point of Sale Location.
            </p>
            <div className="flex justify-center gap-3">
              <ButtonComponent
                variant="outline"
                size="sm"
                onClick={() => setIsUpgradePopup(false)}
              >
                Cancel
              </ButtonComponent>
              <ButtonComponent
                variant="primary"
                size="sm"
                onClick={() => {
                  setIsUpgradePopup(false);
                  window.parent.postMessage({ action: "openAdminPlanModal" }, "*");
                }}
              >
                Upgrade Plan
              </ButtonComponent>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between space-x-3 w-full relative">
        <div className="w-full space-y-3">
          <div className="flex w-full h-full overflow-hidden relative min-w-0">
            <div className="w-full">
              <div className="w-full transition-all duration-100 mb-[10px]">
                <CommonTable
                  tableId="poslocation"
                  columns={columns}
                  data={paginatedLocations}
                  loading={dataLoading}
                  skeletonRows={10}
                  emptyMessage={<NoRecords />}
                  tableHeader={tableHeaderFilter}
                  onSort={SortData}
                  sortBy={sortBy}
                  orderBy={orderBy}
                  tableFooter={
                    locations && totalRecords > 0 ? (
                      <PaginationComponent
                        count={Math.ceil(totalRecords / limitValue) || 1}
                        page={Page}
                        onChange={(e, v) => setPage(v)}
                        totalRecords={totalRecords}
                        startRecord={locations?.length ? (Page - 1) * limitValue + 1 : 0}
                        endRecord={locations?.length ? Math.min(Page * limitValue, totalRecords) : 0}
                        limit={limit}
                        recordsLabel="Records"
                      />
                    ) : null
                  }
                  rowClick={(row) => Handleedit(row?._id)}
                  rowClassName={(row) =>
                    `select-none ${selectedRowsId?.find((x) => x === row?._id) ? "selected-row" : ""}`
                  }
                  containerClassName="pos-location-table sticky-table module-table overflow-x-auto border border-[var(--border-color)] rounded-xl shadow-[var(--shadow-card)]"
                  NEWAPI={handleTableApi}
                  tableDefaults={defaultCustomFields?.defaultPosLocationField}
                  tableWiseAllowedSortKeys={defaultCustomFields?.DEFAULT_POS_LOCATION_SORT_KEYS}
                  userState={userContext}
                />
              </div>
            </div>
            <Popover
              open={filter}
              anchorEl={anchorEl}
              onClose={() => {
                setFilter(false);
                setAnchorEl(null);
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              PaperProps={{
                style: { marginTop: "8px", minWidth: "300px", borderRadius: "8px" },
              }}
              className="rounded-[6px]"
            >
              <div className="p-[20px] filter secondary-bg-color">
                <div className="flex justify-between items-center">
                  <p className="flex font-semibold text-sm m-0">
                    <Filter className="me-1 w-4 h-4" />
                    {t("lang_filter")}
                  </p>
                  <ButtonComponent
                    variant="secondary"
                    size="sm"
                    onClick={(e) => resetFilter(e)}
                  >
                    {t("lang_reset_filter")}
                  </ButtonComponent>
                </div>
                <hr className="main-border-color my-3" />
                <div className="grid gap-y-5">
                  <div>
                    <span className="text-sm table-text font-medium mb-1 inline-block capitalize">
                      {t("lang_search")}
                    </span>
                    <SearchComponent
                      placeholder="Search location"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      onClear={() => {
                        setSearch("");
                        setPage(1);
                      }}
                      iconPlacement="left"
                      isClearable={true}
                    />
                  </div>
                  <div>
                    <span className="text-sm table-text font-medium mb-1 inline-block capitalize">
                      {t("lang_set_items_limit")}
                    </span>
                    <PageLimitDropDownComponent
                      limit={limit}
                      setLimit={setLimit}
                      setPage={setPage}
                      totalRecords={totalRecords}
                    />
                  </div>
                </div>
              </div>
            </Popover>
          </div>
        </div>
      </div>

      {openBulkEditModal && (
        <Dialog
          open={openBulkEditModal}
          disableEscapeKeyDown={true}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            setOpenBulkEditModal(false);
          }}
          fullScreen
          sx={{
            zIndex: 999999,
            "& .MuiDialog-paper": {
              backgroundColor: "var(--primary-bg-color)",
              color: "var(--primary-text-color)",
            },
          }}
        >
          <BulkEditPosLocation
            bulkLocationIds={selectedRowsId}
            onClose={() => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
            }}
            onSuccess={(updatedCount) => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
              setIsNotification(true);
              const count = typeof updatedCount === "number" ? updatedCount : 1;
              setNotificationMsg(
                `${count} Location${count > 1 ? "s" : ""} updated successfully!`
              );
              setSeverity("success");
              getLocations();
            }}
          />
        </Dialog>
      )}
    </div>
  );
};

export default PosLocations;
